/**
 * ActionPlanReportPage — Internal operational CA report
 *
 * Metric definitions (must match backend _build_ca_report_rows exactly):
 *   outstanding        — auditor_assessment ≠ 'implemented'
 *                        (includes unassessed, partial, not-impl, re-completion)
 *   overdue            — outstanding AND by_when < today (independent flag)
 *   awaiting_verification — client_completion_status set AND assessment ≠ 'implemented'
 *   verified_closed    — auditor_assessment = 'implemented' only
 *   original_age_days  — days from first creation (null → "Unknown")
 *   total_closure_days — assessed_at − original creation; verified rows only
 *   verification_delay_days — assessed_at − client_completed_at; verified rows only
 *
 * Canonical CA counting: one row per lineage (carry-forward dedup, Pass 1).
 * Responsible auditor: from Auditor records via job_auditors, not plan creator.
 *   Auditor.user_id links to users.id; Auditor.first_name/last_name for display.
 * Unavailable dates shown as "Unknown", never zero or fabricated.
 * Download CSV: same scoping, filters, and canonical rows as screen.
 *
 * Access: require_internal_user + _require_manager server-side.
 * Assignment scoping: auditor-only users see only their assigned plans.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { actionPlansService } from '../services';
import { ChevronDown, ChevronRight, Filter, RotateCcw, Download } from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (v) => {
  if (!v) return 'Unknown';
  try { return new Date(v).toLocaleDateString(); } catch { return 'Unknown'; }
};

const fmtDays = (v) => {
  if (v === null || v === undefined) return 'Unknown';
  if (v === 0) return '< 1 day';
  return `${v}d`;
};

const STATE_LABELS = {
  outstanding:           'Outstanding',
  overdue:               'Overdue',
  awaiting_verification: 'Awaiting Verification',
  implemented:           'Verified Closed',
  partially_implemented: 'Partially Implemented',
  not_implemented:       'Not Implemented',
  carried_forward:       'Carried Forward',
};

const STATE_STYLE = {
  outstanding:           'bg-amber-100 text-amber-800',
  overdue:               'bg-red-100 text-red-800',
  awaiting_verification: 'bg-blue-100 text-blue-800',
  implemented:           'bg-green-100 text-green-800',
  partially_implemented: 'bg-orange-100 text-orange-800',
  not_implemented:       'bg-red-100 text-red-700',
  carried_forward:       'bg-purple-100 text-purple-800',
};

const StateBadge = ({ ca }) => {
  const key = ca.is_overdue
    ? 'overdue'
    : ca.awaiting_verification
    ? 'awaiting_verification'
    : ca.ca_state || ca.auditor_assessment || 'outstanding';
  return (
    <span
      data-testid={`ca-state-badge-${ca.ca_id}`}
      className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATE_STYLE[key] || 'bg-gray-100 text-gray-700'}`}
    >
      {STATE_LABELS[key] || key}
    </span>
  );
};

// ── Summary card ─────────────────────────────────────────────────────────────

const SummaryCard = ({ label, value, sub, highlight, testId }) => (
  <div
    data-testid={testId}
    className={`rounded-lg border p-4 flex flex-col gap-1 ${highlight ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}
  >
    <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</span>
    <span className={`text-3xl font-bold ${highlight ? 'text-red-700' : 'text-gray-900'}`}>
      {value ?? '—'}
    </span>
    {sub && <span className="text-xs text-gray-500">{sub}</span>}
  </div>
);

// ── Drill-down row ────────────────────────────────────────────────────────────

const CARow = ({ ca }) => {
  const [open, setOpen] = useState(false);
  const auditorNames = ca.responsible_auditors?.length
    ? ca.responsible_auditors.map((a) => a.name).join(', ')
    : 'Unknown';

  const isVerified = ca.auditor_assessment === 'implemented';

  return (
    <>
      <tr
        className="hover:bg-gray-50 cursor-pointer border-b border-gray-100"
        onClick={() => setOpen((v) => !v)}
        data-testid={`ca-row-${ca.ca_id}`}
      >
        <td className="px-3 py-2 text-sm text-gray-500 w-4">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </td>
        <td className="px-3 py-2 text-sm text-gray-700 max-w-xs truncate" title={ca.description}>
          {ca.description || '—'}
        </td>
        <td className="px-3 py-2 text-sm text-gray-600">{ca.site_name || '—'}</td>
        <td className="px-3 py-2 text-sm text-gray-600">{ca.scope || '—'}</td>
        <td className="px-3 py-2 text-sm"><StateBadge ca={ca} /></td>
        <td className="px-3 py-2 text-sm text-gray-600">{fmtDate(ca.by_when)}</td>
        <td className="px-3 py-2 text-sm text-gray-600">{fmtDays(ca.original_age_days)}</td>
        <td className="px-3 py-2 text-sm text-gray-600">
          {ca.awaiting_verification ? fmtDays(ca.awaiting_verification_since_days) : '—'}
        </td>
        <td className="px-3 py-2 text-sm text-gray-600">
          {/* verification_delay_days only shown for verified-closed rows */}
          {isVerified ? fmtDays(ca.verification_delay_days) : '—'}
        </td>
        <td className="px-3 py-2 text-sm text-gray-600">
          {/* total_closure_days only shown for verified-closed rows */}
          {isVerified ? fmtDays(ca.total_closure_days) : '—'}
        </td>
        <td className="px-3 py-2 text-sm text-gray-600 truncate max-w-[160px]" title={auditorNames}>
          {auditorNames}
        </td>
        <td className="px-3 py-2 text-sm">
          <Link
            to={`/action-plans/${ca.plan_id}`}
            className="text-blue-600 hover:underline text-xs"
            onClick={(e) => e.stopPropagation()}
            data-testid={`ca-plan-link-${ca.ca_id}`}
          >
            Plan {ca.plan_id}
          </Link>
        </td>
      </tr>
      {open && (
        <tr
          className="bg-gray-50 border-b border-gray-100"
          data-testid={`ca-detail-${ca.ca_id}`}
        >
          <td />
          <td colSpan={11} className="px-4 py-3">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-1 text-sm text-gray-700">
              <div><span className="font-medium text-gray-500">CA ID: </span>{ca.ca_id}</div>
              <div><span className="font-medium text-gray-500">Stock #: </span>{ca.stock_number || 'Unknown'}</div>
              <div><span className="font-medium text-gray-500">Audit date: </span>{fmtDate(ca.audit_date)}</div>
              <div><span className="font-medium text-gray-500">Responsible (by_who): </span>{ca.by_who || 'Unknown'}</div>
              <div>
                <span className="font-medium text-gray-500">Carry-forward gen: </span>
                {ca.cf_generation === 0 ? 'Original' : `Gen ${ca.cf_generation}`}
              </div>
              <div>
                <span className="font-medium text-gray-500">Client completion: </span>
                {ca.client_completion_status || '—'}
                {ca.client_completed_at ? ` (${fmtDate(ca.client_completed_at)})` : ''}
              </div>
              {ca.auditor_assessment && (
                <>
                  <div>
                    <span className="font-medium text-gray-500">Auditor verdict: </span>
                    {STATE_LABELS[ca.auditor_assessment] || ca.auditor_assessment}
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Assessed: </span>
                    {fmtDate(ca.assessed_at)}
                    {ca.assessed_by_name ? ` by ${ca.assessed_by_name}` : ''}
                  </div>
                  {isVerified && (
                    <div>
                      <span className="font-medium text-gray-500">Verification delay: </span>
                      {fmtDays(ca.verification_delay_days)}
                      {' · '}
                      <span className="font-medium text-gray-500">Total closure: </span>
                      {fmtDays(ca.total_closure_days)}
                    </div>
                  )}
                  {ca.auditor_assessment_note && (
                    <div className="col-span-2 md:col-span-3">
                      <span className="font-medium text-gray-500">Note: </span>
                      {ca.auditor_assessment_note}
                    </div>
                  )}
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ActionPlanReportPage() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const [siteId, setSiteId]               = useState('');
  const [scope, setScope]                 = useState('');
  const [auditorUserId, setAuditorUserId] = useState('');
  const [sortCol, setSortCol]             = useState('original_age_days');
  const [sortDir, setSortDir]             = useState('desc');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (siteId)        params.site_id         = Number(siteId);
      if (scope)         params.scope            = scope;
      if (auditorUserId) params.auditor_user_id  = Number(auditorUserId);
      const result = await actionPlansService.caReport(params);
      setData(result);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [siteId, scope, auditorUserId]);

  useEffect(() => { load(); }, [load]);

  // Filter options derived from scoped rows (auditors that appear in data only)
  const { siteOptions, scopeOptions, auditorOptions } = useMemo(() => {
    if (!data?.rows) return { siteOptions: [], scopeOptions: [], auditorOptions: [] };
    const sites    = new Map();
    const scopes   = new Set();
    const auditors = new Map();
    for (const r of data.rows) {
      if (r.site_id && r.site_name) sites.set(r.site_id, r.site_name);
      if (r.scope) scopes.add(r.scope);
      for (const a of r.responsible_auditors || []) {
        if (a.user_id) auditors.set(a.user_id, a.name);
      }
    }
    return {
      siteOptions:    [...sites.entries()].sort((a, b) => a[1].localeCompare(b[1])),
      scopeOptions:   [...scopes].sort(),
      auditorOptions: [...auditors.entries()].sort((a, b) => a[1].localeCompare(b[1])),
    };
  }, [data]);

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    const col = sortCol;
    return [...data.rows].sort((a, b) => {
      const av = a[col] ?? -1;
      const bv = b[col] ?? -1;
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
  }, [data, sortCol, sortDir]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir('desc'); }
  };

  const ThSort = ({ col, children }) => (
    <th
      className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer select-none hover:text-gray-900"
      onClick={() => toggleSort(col)}
    >
      {children}{sortCol === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );

  // CSV download — same scope/filters as the screen, derived from the same row set
  const handleDownload = () => {
    const params = {};
    if (siteId)        params.site_id         = Number(siteId);
    if (scope)         params.scope            = scope;
    if (auditorUserId) params.auditor_user_id  = Number(auditorUserId);
    const url = actionPlansService.caReportCsvUrl(params);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ca_report_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const s = data?.summary;

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-screen-2xl mx-auto" data-testid="ap-report-page">

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Action Plan — CA Report</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Operational view · one row per canonical action · carry-forward deduplication applied
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="ap-report-download-btn"
            onClick={handleDownload}
            disabled={!data}
            className="flex items-center gap-1.5 text-sm text-white bg-gray-800 hover:bg-gray-700 disabled:opacity-40 px-3 py-1.5 rounded"
          >
            <Download size={14} /> Download CSV
          </button>
          <Link
            to="/action-plans"
            className="text-sm text-blue-600 hover:underline"
            data-testid="ap-report-back-link"
          >
            ← All Action Plans
          </Link>
        </div>
      </div>

      {/* Filter bar */}
      <div
        className="flex flex-wrap gap-3 items-end bg-white border border-gray-200 rounded-lg p-4"
        data-testid="ap-report-filters"
      >
        <Filter size={16} className="text-gray-400 mt-5" />

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">Site</label>
          <select
            data-testid="filter-site"
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1.5 min-w-[160px]"
          >
            <option value="">All sites</option>
            {siteOptions.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">Scope / Service</label>
          <select
            data-testid="filter-scope"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1.5 min-w-[140px]"
          >
            <option value="">All scopes</option>
            {scopeOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">Responsible Auditor</label>
          <select
            data-testid="filter-auditor"
            value={auditorUserId}
            onChange={(e) => setAuditorUserId(e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1.5 min-w-[180px]"
          >
            <option value="">All auditors</option>
            {auditorOptions.map(([uid, name]) => (
              <option key={uid} value={uid}>{name}</option>
            ))}
          </select>
        </div>

        <button
          data-testid="filter-reset-btn"
          onClick={() => { setSiteId(''); setScope(''); setAuditorUserId(''); }}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50"
        >
          <RotateCcw size={13} /> Reset
        </button>
      </div>

      {/* Summary cards */}
      {s && (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
          data-testid="ap-report-summary"
        >
          <SummaryCard
            testId="summary-total"
            label="Total Actions"
            value={s.total_canonical_actions}
          />
          <SummaryCard
            testId="summary-outstanding"
            label="Outstanding"
            value={s.outstanding}
            sub={s.original_age_days_median != null
              ? `Median age: ${fmtDays(Math.round(s.original_age_days_median))}`
              : 'Median age: Unknown'}
          />
          <SummaryCard
            testId="summary-overdue"
            label="Overdue"
            value={s.overdue}
            highlight={s.overdue > 0}
          />
          <SummaryCard
            testId="summary-awaiting"
            label="Awaiting Verification"
            value={s.awaiting_verification}
            sub={s.awaiting_verification_age_days_median != null
              ? `Median wait: ${fmtDays(Math.round(s.awaiting_verification_age_days_median))}`
              : 'Median wait: Unknown'}
          />
          <SummaryCard
            testId="summary-verified"
            label="Verified Closed"
            value={s.verified_closed}
            sub={s.total_closure_days_median != null
              ? `Median closure: ${fmtDays(Math.round(s.total_closure_days_median))}`
              : 'Median closure: Unknown'}
          />
          <SummaryCard
            testId="summary-partial-not"
            label="Partial / Not Impl."
            value={s.partial_not_implemented}
            sub={s.verification_delay_days_median != null
              ? `Median verify delay: ${fmtDays(Math.round(s.verification_delay_days_median))}`
              : undefined}
          />
        </div>
      )}

      {/* Definitions footnote — same for screen and CSV */}
      <div className="text-xs text-gray-400 bg-gray-50 border border-gray-100 rounded px-3 py-2 space-y-0.5">
        <p>
          <strong>Outstanding</strong>: not verified closed (includes unassessed,
          partial, not-implemented, re-completion).{' '}
          <strong>Overdue</strong>: outstanding and past target date (independent).{' '}
          <strong>Awaiting verification</strong>: client completed, auditor assessment pending (independent).{' '}
          <strong>Verified closed</strong>: auditor verdict = Implemented only.
        </p>
        <p>
          <strong>Original age</strong>: days from first creation.{' '}
          <strong>Verification delay</strong>: days from client completion to auditor assessment
          (verified rows only).{' '}
          <strong>Total closure</strong>: days from first creation to assessment
          (verified rows only).{' '}
          Unavailable dates shown as "Unknown".
        </p>
        <p>
          Responsible auditor from job assignment records (Auditor.user_id).
          "Unknown" when no assignment record exists.
          Download CSV uses identical scope, filters, and definitions.
        </p>
      </div>

      {error && (
        <div
          className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700"
          data-testid="ap-report-error"
        >
          {error}
        </div>
      )}

      {loading && (
        <div className="text-sm text-gray-400 py-6 text-center" data-testid="ap-report-loading">
          Loading…
        </div>
      )}

      {!loading && data && (
        <div className="overflow-x-auto rounded-lg border border-gray-200" data-testid="ap-report-table">
          {rows.length === 0 ? (
            <div className="text-sm text-gray-400 py-8 text-center">
              No corrective actions match the current filters.
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-4" />
                  <ThSort col="description">Description</ThSort>
                  <ThSort col="site_name">Site</ThSort>
                  <ThSort col="scope">Scope</ThSort>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <ThSort col="by_when">Target date</ThSort>
                  <ThSort col="original_age_days">Age (d)</ThSort>
                  <ThSort col="awaiting_verification_since_days">Await. verif. (d)</ThSort>
                  <ThSort col="verification_delay_days">Verif. delay (d)</ThSort>
                  <ThSort col="total_closure_days">Closure (d)</ThSort>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Responsible</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Plan</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((ca) => (
                  <CARow key={ca.ca_id} ca={ca} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
